/* eslint-disable no-undef */
/* eslint-disable no-useless-escape */
/**
 * Device Model Extractor Class
 *
 * A class-based utility for extracting device model information
 * across Tizen, Hisense, and WebOS platforms.
 *
 * Includes integrated WebOS utilities for system information retrieval.
 *
 * @example
 * const extractor = new DeviceModelExtractor();
 * const model = await extractor.getDeviceModel('webos');
 *
 * @example
 * const extractor = new DeviceModelExtractor({
 *   transformer: Transformer(),
 *   isTizenDev: false
 * });
 * const model = extractor.getTizenModel();
 */
class DeviceModelExtractor {
  /**
   * Creates an instance of DeviceModelExtractor
   *
   * @param {Object} options - Configuration options
   * @param {Function} [options.transformer] - Transformer instance for country detection (for Hisense)
   * @param {boolean} [options.isTizenDev] - Whether Tizen is in dev mode
   * @param {Object} [options.webapis] - Tizen webapis object (uses window.webapis if not provided)
   * @param {Function} [options.hisenseGetModelName] - Hisense API function (uses window.Hisense_GetModelName if not provided)
   * @param {string} [options.hisenseFallbackModel] - Fallback model for Hisense (default: 'HX65A6801FUWTS_1001')
   */
  constructor(options = {}) {
    this.transformer = options.transformer || null;
    this.isTizenDev = options.isTizenDev || false;
    this.webapis = options.webapis || (typeof window !== 'undefined' ? window.webapis : null);
    this.hisenseGetModelName =
      options.hisenseGetModelName || (typeof window !== 'undefined' && window.Hisense_GetModelName) || null;
    this.hisenseFallbackModel = options.hisenseFallbackModel || 'HX65A6801FUWTS_1001';

    // Cache for Tizen model
    this._tizenModelCache = null;

    // WebOS system info cache
    this._webosSystemInfoInitialized = false;
    this._webosModelCache = null;
  }

  /**
   * Trims minor change codes from device model strings.
   * Example: "HX65A6801FUWTS_1001" -> "HX65A6801FUWTS"
   *
   * @param {string} deviceModel - The device model string
   * @returns {string} - The trimmed device model
   */
  trimMinorChangeCode(deviceModel) {
    if (typeof deviceModel === 'string' && deviceModel.includes('_')) {
      return deviceModel.split('_')[0];
    }
    return deviceModel;
  }

  /**
   * Extracts Tizen device model using webapis.productinfo API
   *
   * @returns {string} - Device model string
   */
  getTizenModel() {
    const apis = this.webapis || (typeof window !== 'undefined' ? window.webapis : null);

    // Dev mode fallback
    if (this.isTizenDev || !apis?.productinfo) {
      return 'dev_UA75NU8000KXZN';
    }

    // Use cache if available
    if (this._tizenModelCache) {
      return this._tizenModelCache;
    }

    // Cache the result to avoid multiple API calls
    if (typeof window !== 'undefined' && !window.realModelCode) {
      window.realModelCode = apis.productinfo.getRealModel();
    }

    this._tizenModelCache = (typeof window !== 'undefined' && window.realModelCode) || apis.productinfo.getRealModel();

    return this._tizenModelCache;
  }

  /**
   * Extracts Hisense device model with country-specific handling
   *
   * @returns {string} - Device model string
   */
  getHisenseModel() {
    const getModelName = this.hisenseGetModelName || (typeof window !== 'undefined' && window.Hisense_GetModelName);

    // Get model name from API or use fallback
    const model =
      getModelName instanceof Function
        ? this.trimMinorChangeCode(getModelName())
        : this.trimMinorChangeCode(this.hisenseFallbackModel);

    // Apply country-specific prefix (Egypt gets ELARABY_ prefix)
    if (this.transformer) {
      try {
        const country = this.transformer.getCountry();
        if (country?.headerCountry === 'EGY') {
          return 'ELARABY_' + model;
        }
      } catch (error) {
        console.warn('Error getting country for Hisense model:', error);
      }
    }

    return model;
  }

  /**
   * Sets WebOS system information (model name, SDK version, UHD support)
   *
   * @param {Function} callback - Optional callback function
   * @returns {Promise} - Promise that resolves when system info is set
   */
  setWebOSSystemInfo(callback) {
    return new Promise((resolve, reject) => {
      if (typeof webOS === 'undefined' || !webOS.service) {
        const error = new Error('WebOS service not available');
        if (callback) callback();
        reject(error);
        return;
      }

      webOS.service.request('luna://com.webos.service.tv.systemproperty', {
        method: 'getSystemInfo',
        parameters: {
          keys: ['modelName', 'sdkVersion', 'UHD'],
        },
        onComplete: inResponse => {
          const isSucceeded = inResponse.returnValue;
          if (isSucceeded) {
            // Cache the model name directly from SDK response
            this._webosModelCache = inResponse.modelName || null;
            this._webosSystemInfoInitialized = true;
          } else {
            console.error('Failed to get TV device information');
          }
          if (callback) callback();
          resolve(inResponse);
        },
      });
    });
  }

  /**
   * Gets WebOS version from user agent
   *
   * @returns {string} - WebOS version ("3", "4", "5", "6", or "7")
   */
  getWebosVersion() {
    const userAgent = navigator.userAgent;
    try {
      const chromeMatch = userAgent.match(/\w*Chrome\w*([^\)|;]+)/g);
      if (!chromeMatch || chromeMatch.length === 0) {
        return '3'; // Default fallback
      }

      const chromeVersion = parseInt(chromeMatch.join('/').split('Chrome/')[1].split(' ')[0]);

      if (isNaN(chromeVersion)) {
        return '3'; // Default fallback
      }

      if (chromeVersion < 53) {
        return '3';
      } else if (chromeVersion >= 53 && chromeVersion < 68) {
        return '4';
      } else if (chromeVersion >= 68 && chromeVersion < 79) {
        return '5';
      } else if (chromeVersion >= 79 && chromeVersion < 87) {
        return '6';
      } else if (chromeVersion >= 87) {
        return '7';
      }
    } catch (error) {
      console.warn('Error parsing WebOS version from user agent:', error);
    }

    return '3'; // Default fallback
  }

  /**
   * Gets Tizen version from user agent
   *
   * @returns {string} - Tizen version ("2.4", "3.0", "4.6", etc.)
   */
  getTizenVersion() {
    const userAgent = navigator.userAgent;
    try {
      const matches = userAgent.match(/Tizen\s(\d+\.\d+)/);
      if (matches && matches.length > 0) {
        return matches[1];
      }
    } catch (error) {
      console.warn('Error parsing Tizen version from user agent:', error);
    }
    return '1.0'; // Default fallback
  }

  /**
   * Gets Hisense version from user agent
   *
   * @returns {string} - Hisense version ("6.0", "7.0", "9.0", etc.)
   */
  getHisenseVersion() {
    const userAgent = navigator.userAgent;
    try {
      const matches = userAgent.match(/VIDAA\/(\d+\.\d+)/);
      if (matches && matches.length > 0) {
        return matches[1];
      }
    } catch (error) {
      console.warn('Error parsing Hisense version from user agent:', error);
    }
    return '1.0'; // Default fallback
  }

  /**
   * Gets WebOS device info using webOS.deviceInfo API
   *
   * @returns {Object|null} - Device info object or null if not available
   */
  getWebosDeviceInfo() {
    if (typeof webOS === 'undefined' || !webOS.deviceInfo) {
      return null;
    }

    let deviceInfo = null;
    webOS.deviceInfo(device => {
      deviceInfo = device;
    });
    return deviceInfo;
  }

  /**
   * Initializes WebOS system information (UDID and system info)
   * Should be called before getting WebOS model
   *
   * @param {Function} callback - Optional callback function
   * @returns {Promise} - Promise that resolves when initialization is complete
   */
  async initializeWebOS(callback) {
    try {
      await this.setWebOSSystemInfo();
      if (callback) callback();
    } catch (error) {
      console.error('Error initializing WebOS:', error);
      if (callback) callback();
      throw error;
    }
  }

  /**
   * Extracts WebOS device model directly from SDK
   * If not cached, makes an async call to the SDK to get the model
   *
   * @returns {Promise<string>} - Promise that resolves with device model string
   */
  getWebOSModel() {
    // Return cached model if available
    if (this._webosModelCache) {
      return Promise.resolve(this._webosModelCache);
    }

    // If already initialized but no cache, return fallback
    if (this._webosSystemInfoInitialized) {
      return Promise.resolve('WebOS_Unknown');
    }

    // Make async call to SDK to get model
    return new Promise(resolve => {
      if (typeof webOS === 'undefined' || !webOS.service) {
        resolve('WebOS_Unknown');
        return;
      }

      webOS.service.request('luna://com.webos.service.tv.systemproperty', {
        method: 'getSystemInfo',
        parameters: {
          keys: ['modelName'],
        },
        onComplete: inResponse => {
          const isSucceeded = inResponse.returnValue;
          if (isSucceeded && inResponse.modelName) {
            // Cache the model
            this._webosModelCache = inResponse.modelName;
            this._webosSystemInfoInitialized = true;
            resolve(inResponse.modelName);
          } else {
            console.error('Failed to get WebOS device model from SDK');
            resolve('WebOS_Unknown');
          }
        },
      });
    });
  }

  /**
   * Universal method to get device model based on platform
   *
   * @param {string} platform - Platform identifier ('tizen', 'vidaa', or 'webos')
   * @param {Object} options - Additional options
   * @param {boolean} options.initializeWebOS - Whether to initialize WebOS before getting model (default: false)
   * @returns {string|Promise<string>} - Device model string (Promise for WebOS)
   */
  getDeviceModel(platform, options = {}) {
    const { initializeWebOS = false } = options;

    switch (platform) {
      case 'tizen':
        return this.getTizenModel();

      case 'vidaa':
        return this.getHisenseModel();

      case 'webos':
        if (initializeWebOS) {
          return this.initializeWebOS().then(() => this.getWebOSModel());
        }
        // WebOS model extraction is always async (calls SDK)
        return this.getWebOSModel();

      default:
        console.warn(`Unsupported platform: ${platform}. Supported platforms: tizen, vidaa, webos`);
        return 'UNKNOWN_PLATFORM';
    }
  }

  /**
   * Safe method to get device model with error handling
   *
   * @param {string} platform - Platform identifier
   * @param {Object} options - Additional options
   * @returns {string|Promise<string>} - Device model string with fallback handling
   */
  async getDeviceModelSafe(platform, options = {}) {
    try {
      const result = this.getDeviceModel(platform, options);

      // Handle Promise if WebOS initialization is needed
      if (result instanceof Promise) {
        const model = await result;
        return model || 'UNKNOWN_DEVICE_MODEL';
      }

      return result || 'UNKNOWN_DEVICE_MODEL';
    } catch (error) {
      console.error('Error extracting device model:', error);
      return 'ERROR_EXTRACTING_MODEL';
    }
  }
}

export default DeviceModelExtractor;
